import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  ButtonResponsiveWrapper,
  CheckBox,
  getDuration,
  Inscription,
  Preloader,
  useResponsive,
} from '@common-fe/common-fe';
import Lottie from 'lottie-react';

import GainingFullAccessIcon from '@/assets/bank-icons/gaining-full-access.json';
import { MINUTE } from '@/common/constants';
import { ModalWrapper } from '@/components/wrappers';
import { useVerifyCard } from '@/modules/core/components/Dashboard/components/BannersList/CompleteBanner/components/VerifyMyCardModal/queries';
import {
  ATTEMPTS_LIMIT,
  CardActivationVariants,
  Steps,
} from '@/modules/core/components/Dashboard/components/BannersList/CompleteBanner/components/VerifyMyCardModal/VerifyMyCardModal.constants';
import { CardActivationData } from '@/modules/transaction/components/Cards/Cards.types';
import { useRequestedActivationCards } from '@/modules/transaction/components/Cards/components/CardActivationModals/hooks/useRequestedActivationCards';
import { useCardActivationQuery } from '@/modules/transaction/components/Cards/components/CardActivationModals/queries/useCardActivation.query';
import { useRefreshToken } from '@/modules/user/components/SignIn/hooks';
import theme from '@/styles/theme';

import CardDigitsField from './CardDigitsField';

const BUTTON_WIDTH = '160px';
const CARD_NUMBERS_LENGTH = 4;
const LIMIT_REACHED_STATUS_CODE = 429;
const FIVE_MINUTES = 5 * MINUTE;

interface Props {
  setStep: (step: Steps) => void;
  onSetVisible: (value: boolean) => void;
  setVerificationRequestsLimit: (value: number) => void;
  cardActivationData?: CardActivationData;
  setIsActivationSuccess?: (value: CardActivationVariants) => void;
  attempt: number;
  setAttempt: (value: number) => void;
  setAttemptLimitTimer: (value: number) => void;
  onSuccess?: () => void;
}

const EnterCardDigitsModal: React.FC<Props> = ({
  setStep,
  onSetVisible,
  cardActivationData,
  setIsActivationSuccess,
  setVerificationRequestsLimit,
  attempt,
  setAttempt,
  setAttemptLimitTimer,
  onSuccess,
}) => {
  const { isMobile } = useResponsive();
  const [cardNumbers, setCardNumbers] = useState('');
  const [isChecked, setIsChecked] = useState(false);
  const { t } = useTranslation();
  const { handleActivate: handleCardActivate, isLoading: isCardActivationLoading } = useCardActivationQuery({
    memberSequenceIdentifier: cardActivationData?.memberSequenceIdentifier,
    alternateAccountId: cardActivationData?.alternateAccountId,
    tokenCardId: cardActivationData?.tokenCardId,
  });

  const { setRequestedActivationCards } = useRequestedActivationCards();

  const isCardNumberValid = useMemo(() => cardNumbers.length === CARD_NUMBERS_LENGTH, [cardNumbers]);

  const handleActivate = useCallback(async () => {
    if (!isChecked) return;

    await handleCardActivate();
    setIsActivationSuccess?.(CardActivationVariants.ACTIVATED);
    setRequestedActivationCards(cardActivationData?.cardId || '');
  }, [
    cardActivationData,
    handleCardActivate,
    isChecked,
    setIsActivationSuccess,
    setRequestedActivationCards,
  ]);

  const { handleRefreshToken } = useRefreshToken();
  const { onVerify, isLoading } = useVerifyCard({
    last4: cardNumbers,
    onSuccess: () => {
      // TODO: Remove timeout after fix on backend
      setTimeout(
        async () => {
          try {
            await handleRefreshToken();
          } finally {
            window.location.reload();
          }
          setTimeout(
            async () => {
              try {
                await handleRefreshToken();
              } finally {
                window.location.reload();
              }
            },
            getDuration({ seconds: 3 })
          );
        },
        getDuration({ seconds: 1 })
      );
    },
  });

  const handleSubmit = useCallback(async () => {
    if (isCardNumberValid && !isLoading) {
      try {
        await onVerify();
      } catch (error) {
        // @ts-ignore
        const statusCode = error?.response?.status;
        if (statusCode === LIMIT_REACHED_STATUS_CODE) {
          setVerificationRequestsLimit(FIVE_MINUTES);
          setStep(Steps.limitReachedError);
        } else {
          if (attempt === 0) {
            setAttemptLimitTimer(FIVE_MINUTES);
          }
          const currentAttempt = attempt + 1;
          setAttempt(currentAttempt);
          if (currentAttempt >= ATTEMPTS_LIMIT) {
            setStep(Steps.limitReachedError);
            setVerificationRequestsLimit(FIVE_MINUTES);
          } else {
            setStep(Steps.verificationError);
          }
        }
        return;
      }

      try {
        await handleActivate();
        if (onSuccess) {
          onSuccess();
        } else {
          setStep(Steps.successActivated);
        }
      } catch {
        setIsActivationSuccess?.(CardActivationVariants.ERROR_ACTIVATION);
        setStep(Steps.successActivated);
      }
    }
  }, [
    onSuccess,
    onVerify,
    isCardNumberValid,
    isLoading,
    setStep,
    handleActivate,
    setVerificationRequestsLimit,
    setIsActivationSuccess,
    attempt,
    setAttempt,
    setAttemptLimitTimer,
  ]);

  const handleClose = useCallback(() => onSetVisible(false), [onSetVisible]);

  const isCurrentLoading = useMemo(
    () => isCardActivationLoading || isLoading,
    [isCardActivationLoading, isLoading]
  );

  return (
    <ModalWrapper visible onSetVisible={onSetVisible} testId="enter-card-digits">
      <Box margin={{ top: isMobile ? 'spacing8' : 'spacing12' }} align="center">
        <Lottie
          animationData={GainingFullAccessIcon}
          style={{
            width: isMobile ? '120px' : undefined,
            height: isMobile ? '120px' : undefined,
          }}
        />
        <Inscription
          margin={{ top: 'spacing8', horizontal: isMobile ? 'spacing12' : undefined }}
          size={isMobile ? theme.spacings.spacing20 : theme.spacings.spacing24}
          weight={700}
          textAlign="center"
        >
          {t('Enter the last 4 digits of your payment card')}
        </Inscription>
      </Box>

      <Box
        margin={{ top: isMobile ? 'spacing16' : 'spacing24' }}
        round="container1Round"
        pad={isMobile ? 'spacing16' : 'spacing24'}
        background="module"
      >
        <CardDigitsField
          title={t('The last 4 digits of your card')}
          length={CARD_NUMBERS_LENGTH}
          onChangeValue={setCardNumbers}
        />
      </Box>

      <Box
        direction={isMobile ? 'column' : 'row'}
        justify={cardActivationData ? 'between' : 'end'}
        align={isMobile ? 'stretch' : 'center'}
        margin={{ vertical: isMobile ? 'spacing16' : 'spacing24' }}
      >
        {!!cardActivationData && (
          <Box margin={isMobile ? { bottom: 'spacing16' } : {}}>
            <CheckBox
              checked={isChecked}
              onChange={() => setIsChecked(!isChecked)}
              label={
                <Inscription size="medium" alignSelf="center">
                  {t('Activate my card once verified')}
                </Inscription>
              }
            />
          </Box>
        )}
        <ButtonResponsiveWrapper align="center" justify="center" fill={isMobile}>
          <AppButton
            fill={isMobile}
            testId="close"
            width={BUTTON_WIDTH}
            buttonType="secondary"
            onClick={handleClose}
          >
            {t('Cancel')}
          </AppButton>

          <AppButton
            fill={isMobile}
            width={BUTTON_WIDTH}
            onClick={handleSubmit}
            disabled={!isCardNumberValid}
            testId="submit"
          >
            {isCurrentLoading ? <Preloader color="white" /> : t('Submit')}
          </AppButton>
        </ButtonResponsiveWrapper>
      </Box>
    </ModalWrapper>
  );
};

export default EnterCardDigitsModal;
